import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Bell, Megaphone, CalendarBlank, Users, Table, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_CONFIG = {
  announcement: { icon: Megaphone, color: "#E07A5F", label: "Announcement" },
  reservation: { icon: CalendarBlank, color: "#2F4F4F", label: "Reservation" },
  walk_in: { icon: Users, color: "#F4A261", label: "Walk-In" },
  table_assigned: { icon: Table, color: "#81B29A", label: "Table Assigned" },
  chat: { icon: ChatCircleDots, color: "#3D405B", label: "Chat" },
  system: { icon: Bell, color: "#9CA3AF", label: "System" },
};

const to12h = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const h = d.getHours();
  const m = String(d.getMinutes()).padStart(2, "0");
  const suffix = h >= 12 ? "PM" : "AM";
  const display = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${display}:${m} ${suffix}`;
};

export default function NotificationsPage() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [showAnnounce, setShowAnnounce] = useState(false);
  const [announceTitle, setAnnounceTitle] = useState("");
  const [announceBody, setAnnounceBody] = useState("");
  const [announceTarget, setAnnounceTarget] = useState("all");
  const isAdmin = user?.role === "admin";

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications?limit=100");
      setNotifications(data);
    } catch { /* silent */ }
  }, []);

  const markRead = useCallback(async () => {
    try {
      await api.put("/notifications/read");
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchNotifications(); markRead(); }, [fetchNotifications, markRead]);

  // WebSocket
  useEffect(() => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");
    if (!wsUrl) return;
    let ws;
    try {
      ws = new WebSocket(`${wsUrl}/api/ws`);
      ws.onmessage = (e) => {
        const msg = JSON.parse(e.data);
        if (msg.type === "notification") {
          fetchNotifications();
          markRead();
        }
      };
    } catch { /* silent */ }
    return () => ws?.close();
  }, [fetchNotifications, markRead]);

  const sendAnnouncement = async () => {
    if (!announceTitle.trim()) return;
    try {
      const target_roles = announceTarget === "all" ? [] : [announceTarget];
      await api.post("/notifications/announce", { title: announceTitle.trim(), body: announceBody.trim(), target_roles });
      setShowAnnounce(false);
      setAnnounceTitle("");
      setAnnounceBody("");
      fetchNotifications();
      toast.success("Announcement sent");
    } catch { toast.error("Failed to send"); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Notifications</h2>
          <p className="text-xs text-stone-500 mt-1">{notifications.length} notifications</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowAnnounce(true)} style={{ backgroundColor: "#2F4F4F" }} data-testid="send-announce-btn">
            <Megaphone size={16} className="mr-1.5" /> Announce
          </Button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-16">
          <Bell size={48} className="mx-auto mb-3 text-stone-300" weight="duotone" />
          <p className="text-stone-500 text-sm">No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n, idx) => {
            const cfg = TYPE_CONFIG[n.type] || TYPE_CONFIG.system;
            const Icon = cfg.icon;
            return (
              <div key={n.id} className="bg-white rounded-lg border border-stone-200 p-3 sm:p-4 flex items-start gap-3 animate-fade-in" style={{ animationDelay: `${idx * 30}ms` }} data-testid={`notif-${n.id}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: cfg.color + "15" }}>
                  <Icon size={16} weight="duotone" style={{ color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-medium text-sm text-stone-800">{n.title}</span>
                    <Badge style={{ backgroundColor: cfg.color + "15", color: cfg.color, border: "none" }} className="text-[9px]">{cfg.label}</Badge>
                  </div>
                  {n.body && <p className="text-xs text-stone-500">{n.body}</p>}
                  <p className="text-[10px] text-stone-400 mt-1">{to12h(n.created_at)}{n.sender_name ? ` · ${n.sender_name}` : ""}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Announcement Dialog */}
      <Dialog open={showAnnounce} onOpenChange={setShowAnnounce}>
        <DialogContent data-testid="announce-dialog">
          <DialogHeader><DialogTitle>Send Announcement</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Title</Label>
              <Input value={announceTitle} onChange={(e) => setAnnounceTitle(e.target.value)} placeholder="Announcement title" className="mt-1.5" data-testid="announce-title" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Message</Label>
              <Input value={announceBody} onChange={(e) => setAnnounceBody(e.target.value)} placeholder="Details..." className="mt-1.5" data-testid="announce-body" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Send To</Label>
              <Select value={announceTarget} onValueChange={setAnnounceTarget}>
                <SelectTrigger className="mt-1.5" data-testid="announce-target"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Everyone</SelectItem>
                  <SelectItem value="server">Servers Only</SelectItem>
                  <SelectItem value="host">Hosts Only</SelectItem>
                  <SelectItem value="admin">Admins Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={sendAnnouncement} className="w-full" style={{ backgroundColor: "#2F4F4F" }} data-testid="send-announce-submit">Send Announcement</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
